import React, {useContext, useEffect, useState } from "react";
import {Box, Button, Dialog, DialogActions, DialogContent, Paper, useTheme} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {FormProvider, useFieldArray, useForm, useWatch} from "react-hook-form";
import {useRecoilState, useRecoilValue, useSetRecoilState} from "recoil";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup";
import { useMutation, useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import SectionHeader from "../../../util/component/SectionHeader";
import ContentBody from "../common/content-body/ContentBody";
import {IExtensionManagementForm} from "../models/models";
import RaInfo from "./ra-info/RaInfo";
import RecapInfo from "./recap-info/RecapInfo";
import ExtensionInfo from "./extension-info/ExtensionInfo";
import { EXTENSION_MANAGEMENT_FORM_VALIDATION_SCHEMA, EXTENSION_MANAGEMENT_INITIAL_VALUE_FORM } from "./config";
import {dmsTimeFramesState, extensionDaysState, extensionManagementRaFetchedDataState} from "./state";
import {createExtension, fetchDmsTimeFrames, fetchRaData, saveExtension} from "./services/http.services";
import CustomLoader from "../../../util/component/CustomLoader";
import { GlobalContext } from "../../../util/state/GlobalContextProvider";
import { snackbarTypes } from "../../../util/state/globalVariables";
import {AxiosError} from "axios";
import {CREDIT_TYPE_LABELS, CREDIT_TYPE_LIST} from "../config";
import {createSaveErrorsManagement} from "./services/utils";

const ExtensionManagement = () => {
    const theme = useTheme();
    const {t} = useTranslation(['network', 'translation']);
    const navigate = useNavigate();

    const { globalData, dispatchGlobalData } = useContext(GlobalContext);
    const extensionDaysData = useRecoilValue<any>(extensionDaysState);
    const setDmsTimeFrames = useSetRecoilState<any>(dmsTimeFramesState);
    const [emraFetchedDataState, setEMRAFetchedDataState] = useRecoilState(extensionManagementRaFetchedDataState);

    const [open, setOpen] = useState(false);

    const params = useParams();

    const creditTypeLabels: any = CREDIT_TYPE_LABELS;

    const raExtensionForm = useForm<IExtensionManagementForm>({
        defaultValues: EXTENSION_MANAGEMENT_INITIAL_VALUE_FORM,
        resolver: yupResolver(
            EXTENSION_MANAGEMENT_FORM_VALIDATION_SCHEMA
        )
    });

    const requestedDropoffDates = useFieldArray<IExtensionManagementForm>({
        control: raExtensionForm.control,
        name: "requestedDropoffDateTimeList"
    });

    const extensionPeriodValue = useWatch<IExtensionManagementForm>({
        control: raExtensionForm.control,
        name: "extensionPeriod"
    });

    const viewDefaultError = (mess: string) => {
        dispatchGlobalData({
            type: 'SET_SNACKBAR',
            payload: {
                snackbar: {
                    message: t(mess),
                    type: snackbarTypes.error,
                    show: true,
                    autoClose: true,
                }
            }
        });
    }

    /**
     *  Save Extension
     */
    const handleSaveExtension = (e: React.SyntheticEvent) => {
        e.stopPropagation();
        setOpen(false);
        const data: any = raExtensionForm.getValues();
        if (!emraFetchedDataState?.ntwOfferId) {
            const payload: any = {
                idRentalAgreement: params.id as string,
                raState: {code: "ONGOING", description: "ONGOING", label:"ONGOING"},
                extensionInfo: [{
                    raNumber:                       emraFetchedDataState.raNumber,
                    requestExtensionDropOffDate:    data.requestedDropoffDateTime?.toISOString(),
                    billingOwner:                   data.billingOwner,
                    renewedByExtension:             {code: extensionDaysData.renewedByExtension, description: extensionDaysData.renewedByExtension},
                    expectedDropOffDate:            !!emraFetchedDataState.checkIn.expectedCheckInDate ? emraFetchedDataState.checkIn.expectedCheckInDate : null,
                    expectedDropOffLocation:        emraFetchedDataState.bookingDetail.dropOffLocation,
                    daysOverdue: null,
                    lastModifiedBy:                 data.lastModifiedBy,
                    extendedTimestamp:              data.extensionTimestamp?.toISOString(),
                    extensionPeriod:                extensionDaysData?.extraDays,
                    extraRentalDays:                extensionDaysData?.extraDays,
                    extensionRentalAmount:          !!extensionDaysData?.extensionRentalAmount ? extensionDaysData.extensionRentalAmount : 0,
                    extensionAncillaryAmount:       !!extensionDaysData?.extensionAncillaryAmount ? extensionDaysData.extensionAncillaryAmount : 0,
                    extensionTotalAmount:           !!extensionDaysData?.extensionTotalAmount ? extensionDaysData.extensionTotalAmount : 0
                }]
            };

            saveExtensionMutation.mutate(payload);
        } else {
            const payload: any = {
                idRentalAgreement:              params.id as string,
                billingOwnerId:                 data.billingOwner.code,
                extensionAncillaryAmount:       !!extensionDaysData?.extensionAncillaryAmount ? extensionDaysData.extensionAncillaryAmount : 0,
                extensionPeriod:                extensionDaysData?.extraDays,
                extensionRentalAmount:          !!extensionDaysData?.extensionRentalAmount ? extensionDaysData.extensionRentalAmount : 0,
                extensionTotalAmount:           !!extensionDaysData?.extensionTotalAmount ? extensionDaysData.extensionTotalAmount : 0,
                extraRentalDays:                extensionDaysData?.extraDays,
                renewedByExtension:             extensionDaysData.renewedByExtension,
                effectiveReturnDate:            data.requestedDropoffDateTime?.toISOString(),
            };

            createExtensionMutation.mutate(payload);
        }
    };

    const handleCancel = () => {
        resetFields(raExtensionForm);
        raExtensionForm.clearErrors();
        setOpen(false);
    }

    const handleBack = () => {
        if (params.id) {
            navigate(`/network/ra-detail/${params.id}`);
        } else {
            navigate('/network/ra-search');
        }
    }

    const {isFetching, refetch} = useQuery(['extensionManagement', {idRentalAgreement: params.id}], () => fetchRaData({idRentalAgreement: params.id}), {
        enabled: false,
        onSuccess: (response: any) => {
            if (!!response) {
                let pickUpDate;
                if (!!response.checkOut.effectiveCheckoutDate && response.checkOut.effectiveCheckoutDate > response.raScanGenerationDate) {
                    pickUpDate = response.checkOut.effectiveCheckoutDate;
                }
                else {
                    pickUpDate = response.raScanGenerationDate;
                }

                // set raInfo
                raExtensionForm.setValue('raNumber',  response.raNumber);
                raExtensionForm.setValue('dmsIdentificationCode',  response.checkOut.dmsIdentificationCode);
                raExtensionForm.setValue('pickUpDate',  pickUpDate);
                raExtensionForm.setValue('renewalsAndSubstitutions',  response.renewalsSubstitution);
                raExtensionForm.setValue('pickupLocation',  response.bookingDetail.pickUpLocation);
                raExtensionForm.setValue('expectedDropoffDate',  !!response.checkIn.expectedCheckInDate ? dayjs(response.checkIn.expectedCheckInDate) : null); // by Pirone 13/06/2024
                raExtensionForm.setValue('expectedDropoffLocation',  response.bookingDetail.dropOffLocation);

                if (response.financialInfo.segment === 'COR' || (response.financialInfo.segment === 'REP' && response.financialInfo.category === '22')) {
                    const pud = new Date(pickUpDate);
                    pud.setDate(pud.getDate() + 360);
                    // @ts-ignore
                    raExtensionForm.setValue('requestedDropoffDateTime', dayjs(pud));
                }

                requestedDropoffDates.remove();
                response.extensionInfo
                    .forEach((extension: any) => {
                        requestedDropoffDates
                            .append(dayjs(extension.requestExtensionDropOffDate));
                    });

                // set recapInfo
                raExtensionForm.setValue('lastModifiedBy',  globalData.user.username);
                raExtensionForm.setValue('creditType', CREDIT_TYPE_LIST.includes(response?.financialInfo?.creditType) ? creditTypeLabels[response?.financialInfo?.creditType] : '');
                raExtensionForm.setValue('reservedPricing', !!response?.financialInfo?.reservedTariffs);
                setEMRAFetchedDataState(response);
            }
        },
        onError: (error) => {
            viewDefaultError('translation:error.tryLater');

            navigate(-1);
        }
    })

    const fetchDmsTimeFramesQuery = useQuery(['dmsTimeFrames', {dmsCode: emraFetchedDataState?.checkIn?.dmsIdentificationCode?.code}],
        () => fetchDmsTimeFrames({dmsCode: emraFetchedDataState?.checkIn?.dmsIdentificationCode?.code}),
        {
            enabled: false,
            onSuccess: (response: any) => {
                setDmsTimeFrames(response);
            },
            onError: (error) => {
                viewDefaultError(createSaveErrorsManagement('translation:error.tryLater'));
            }
        }
    );

    /**
     * Save Extension (used for RA without offerId)
     */
    const saveExtensionMutation = useMutation(
        saveExtension,
        {
            onSuccess: () => {
                navigate(`/network/ra-detail/${params.id}`);
            },
            onError: (error: AxiosError) => {
                viewDefaultError(createSaveErrorsManagement(error));
            }
        }
    );

    const createExtensionMutation = useMutation(
        createExtension,
        {
            onSuccess: () => {
                navigate(`/network/ra-detail/${params.id}`);
            },
            onError: (error: AxiosError) => {
                viewDefaultError(createSaveErrorsManagement(error));
            }
        }
    );

    const handleClose = () => {
        setOpen(false);
    }
    
    const handleOpen = (data: any) => {
        setOpen(true);
    }

    useEffect(() => {
        refetch();
    }, [])

    useEffect(() => {
        if (!!emraFetchedDataState) {
            fetchDmsTimeFramesQuery.refetch();
        }
    }, [emraFetchedDataState])

    return (
        <>
            <SectionHeader title={t('extensionManagement.header.title')} />

            <ContentBody>
                <Paper elevation={1} sx={{p: theme.spacing(2), mb: theme.spacing(2)}}>
                    <FormProvider {...raExtensionForm}>

                        <RaInfo />

                        <ExtensionInfo />

                        <RecapInfo />

                        <Box sx={{display: "flex", justifyContent: "space-between", mt: theme.spacing(2)}}>
                            <Button variant={"outlined"} color={'secondary'} onClick={handleBack}>
                                {t('extensionManagement.button.back')}
                            </Button>

                            <Box>
                                <Button variant={"outlined"} color={"accent"}
                                        sx={{mx: theme.spacing(1)}}
                                        onClick={handleCancel}>
                                    {t('extensionManagement.button.cancel')}
                                </Button>
                                <Button color={"accent"}
                                        sx={{ml: theme.spacing(1)}}
                                        onClick={raExtensionForm.handleSubmit(handleOpen)}
                                        disabled={!extensionPeriodValue}>
                                    {t('extensionManagement.button.continue')}
                                </Button>
                            </Box>
                        </Box>

                        <Dialog
                            open={open}
                            onClose={handleClose}
                            aria-labelledby="alert-dialog-title"
                            aria-describedby="alert-dialog-description"
                        >
                            <DialogContent id="alert-dialog-title">
                                {t('extensionManagement.confirmDialog.content')}
                            </DialogContent>
                            <DialogActions>
                                <Button variant={"outlined"} onClick={handleCancel}>{t('extensionManagement.confirmDialog.buttons.cancel')}</Button>
                                <Button onClick={handleSaveExtension}>{t('extensionManagement.confirmDialog.buttons.continue')}</Button>
                            </DialogActions>
                        </Dialog>
                    </FormProvider>
                </Paper>
            </ContentBody>

            <CustomLoader loading={isFetching || saveExtensionMutation.isLoading || createExtensionMutation.isLoading || fetchDmsTimeFramesQuery.isFetching}/>
        </>
    );
}

export default ExtensionManagement;

export const resetFields = (raExtensionForm: any, all = true) => {
    raExtensionForm.setValue('extensionPeriod',  '');
    raExtensionForm.setValue('renewedByExtension',  '');
    raExtensionForm.setValue('creditType',  '');
    raExtensionForm.setValue('reservedPricing',  false);
    raExtensionForm.setValue('rentalExtensionAmount',  '');
    raExtensionForm.setValue('ancillariesExtensionAmount',  '');
    raExtensionForm.setValue('totalExtensionAmount',  '');
    raExtensionForm.setValue('extensionTimestamp',  null);

    if (all) {
        raExtensionForm.setValue('requestedDropoffDateTime',  null);
        raExtensionForm.setValue('billingOwner',  null);
    }
}